import { THUMBNAIL_TRANSFORMS, THUMBNAIL_WIDTHS } from '@/constants/search'
import { formatRecipeTime } from '../format-recipe-time'
import { buildSrc, buildSrcSetUrls, type ExtendedSanityImageSource } from '../media-transformer'
import type { MappedSKU } from '@/clients/commercelayer/apis/get-skus'

interface SuggestionsHit {
  pages: {
    exact_nb_hits: number
  }
  link?: {
    href: string
    text: string
  }
  query: string
  _highlightResult: {
    query?: {
      value: string
    }
    title?: {
      value: string
    }
  }
}

interface SearchResultFeature {
  _type: string
  _id: string
  link: {
    href: string
  }
  title: string
  copy?: string
  image: ExtendedSanityImageSource
  youtubeId?: string
  price?: MappedSKU['price']
}

const svgIcon = `
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    class="mr-4"
    data-icon="timer"
  >
    <g fill="#337179">
      <path d="M10.83 13.179c-.516-.541-.412-1.264.208-1.694l3.45-2.411c.327-.228.666.117.438.436l-2.407 3.447c-.437.62-1.161.73-1.69.222Z"></path><path fill-rule="evenodd" d="M12 18a6 6 0 1 0 0-12 6 6 0 0 0 0 12Zm0 1.5a7.5 7.5 0 1 0 0-15 7.5 7.5 0 0 0 0 15Z" clip-rule="evenodd"></path>
    </g>
  </svg>`

export const mapSearchResultFeature = ({
  _type,
  _id,
  link,
  title = '',
  price,
  copy,
  image,
  youtubeId,
}: SearchResultFeature) => {
  let img
  let cardCopy = 'Read more'
  let isVideo = false

  switch (_type) {
    case 'page.recipe':
      cardCopy = formatRecipeTime(copy || 'View recipe')
      break
    case 'page.videos':
      cardCopy = 'Watch video'
      img = {
        src: `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`,
        alt: title,
      }
      isVideo = true
      break
    case 'page.product':
      cardCopy = price?.formattedAmount || ''
      break
  }

  const copyHtml = `${_type === 'page.recipe' ? svgIcon : ''} <span>${cardCopy}</span>`
  const href = link?.href || ''

  if (image?.asset?._ref) {
    img = {
      src: buildSrc(image, THUMBNAIL_TRANSFORMS)?.vanityUrl(),
      srcset: buildSrcSetUrls(image, THUMBNAIL_TRANSFORMS, THUMBNAIL_WIDTHS),
      alt: image.alt || title,
    }
  }

  return {
    _id,
    href,
    title,
    copyHtml,
    img,
    isVideo,
  }
}

const buildLinkHtml = (value: string, href: string, count?: number) => {
  const highlightEl = `<span>${value}</span>`
  const countEl = count ? `<span class="type-body-sm text-forest">(${count})</span>` : ''
  return `<a href="${href}" class="card-link">${highlightEl}${countEl}</a>`
}

export const mapSearchResultSuggestion = ({
  query,
  pages,
  _highlightResult,
}: SuggestionsHit): string => {
  const value = _highlightResult?.query?.value || ''
  const href = `/search/${query}`
  const count = pages?.exact_nb_hits

  return buildLinkHtml(value, href, count)
}

export const mapSearchResultCollection = ({ link, _highlightResult }: SuggestionsHit): string => {
  const value = _highlightResult?.title?.value || ''
  const href = link?.href || ''

  return buildLinkHtml(value, href)
}
